import { analyzeXbrlBySheets } from "@/apis/dsd/route";
import {
  GetTaskStatusResponse,
  SheetTaskStatusItem,
  TaskStatusValue,
} from "@/types/dsd";
import { useCategoryId, useWorkbookId } from "@/hooks/use-workbook";
import queries from "@/queries";
import { useMappingStore } from "@/stores/mapping";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "sonner";
import {
  checkAnalyzingFinished,
  getSheetIdToStatusMap,
  getSheetIdToTaggingTargetMap,
} from "./task-status";

// 기존 import 경로를 유지한다 — 파생 로직만 side effect 없는 모듈로 옮겼다.
export {
  checkAnalyzingFinished,
  getSheetIdToStatusMap,
  getSheetIdToTaggingTargetMap,
};

const TASK_STATUS_POLL_INTERVAL_MS = 5000;

export const useDsdCategories = (fileId: string) => {
  const {
    data: categories,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    ...queries.workbook(fileId!)._ctx.categories(),
    enabled: false,
    select: (data) => {
      const sortedCategories = [...data.categories].sort((a, b) =>
        a.category.localeCompare(b.category, "ko", { numeric: true }),
      );
      return sortedCategories.map((category) => ({
        ...category,
        tables: [...category.tables].sort((a, b) =>
          a.name.localeCompare(b.name, "ko", { numeric: true }),
        ),
      }));
    },
  });

  return { data: categories, isLoading, isError, refetch };
};

export const useDsdTaskStatus = (
  workbookId: string | undefined,
  options?: { enablePolling?: boolean },
) => {
  const enablePolling = options?.enablePolling ?? true;
  const { data } = useQuery({
    ...queries.workbook(workbookId ?? "")._ctx.taskStatus(),
    enabled: !!workbookId,
    // 데이터 기반으로 polling 제어: 분석 종료 시 polling 중지
    refetchInterval: (query) => {
      if (!enablePolling) return false;
      const queryData = query.state.data as GetTaskStatusResponse | undefined;
      if (queryData && checkAnalyzingFinished(queryData)) {
        return false;
      }
      return TASK_STATUS_POLL_INTERVAL_MS;
    },
    gcTime: 0,
    staleTime: 0,
  });
  return { data };
};

export const useCurrentAnalyzeStatus = () => {
  const { data: workbookId } = useWorkbookId();
  const { data: categoryId } = useCategoryId();
  const { data: taskStatusData } = useDsdTaskStatus(workbookId ?? undefined);

  const sheetIdToStatus = useMemo(
    () => (taskStatusData ? getSheetIdToStatusMap(taskStatusData) : {}),
    [taskStatusData],
  );

  const selectedFact = useMappingStore((state) => state.fact.selected);

  const status = selectedFact ? sheetIdToStatus[categoryId ?? ""] : null;

  return status;
};

// 테이블 단위 XBRL 재분석 mutation
export const useRetryAnalyzeXbrl = () => {
  const queryClient = useQueryClient();
  const { data: workbookId } = useWorkbookId();

  return useMutation({
    mutationFn: (sheetIds: string[]) => {
      if (!workbookId) throw new Error("workbookId is required");
      if (sheetIds.length === 0) return Promise.resolve();
      return analyzeXbrlBySheets(workbookId, sheetIds);
    },
    // 요청이 분석 끝까지 블로킹이라 그동안 서버 상태는 그대로다. 낙관적으로 RUNNING 을
    // 써넣지 않으면 폴링이 멈춘 채로 남아 진행 표시가 안 뜨고, 이 시트를 대상으로 한
    // 오토태깅·롤포워드 가드(useIsCurrentSheetAnalyzing)도 풀린다.
    onMutate: (sheetIds: string[]) => {
      if (!workbookId) return;
      queryClient.setQueryData(
        queries.workbook(workbookId)._ctx.taskStatus().queryKey,
        (old: GetTaskStatusResponse | undefined) => {
          if (!old) return old;
          return {
            ...old,
            sheets: old.sheets.map((sheet: SheetTaskStatusItem) =>
              sheetIds.includes(sheet.sheetId)
                ? { ...sheet, status: "RUNNING" as TaskStatusValue }
                : sheet,
            ),
          };
        },
      );
    },
    onError: () => {
      toast.error("분석에 실패했습니다. 잠시 후 다시 시도해주세요.");
      if (!workbookId) return;
      // 낙관적으로 써넣은 RUNNING 을 서버 상태로 되돌린다. 안 되돌리면 분석 중이 아닌
      // 시트가 계속 진행 중으로 보인다.
      queryClient.invalidateQueries(
        queries.workbook(workbookId)._ctx.taskStatus(),
      );
    },
    onSuccess: () => {
      if (!workbookId) return;
      // taskStatus 쿼리 invalidate하여 polling 재시작
      queryClient.invalidateQueries(
        queries.workbook(workbookId)._ctx.taskStatus(),
      );
      // 즉시 refetch하여 매핑 뷰 등 구독자가 갱신된 status를 받도록 함
      queryClient.refetchQueries(
        queries.workbook(workbookId)._ctx.taskStatus(),
      );
    },
  });
};
